/**
 * RadialGraph.tsx - Radial/circular graph layout using D3
 * 
 * CHANGE HISTORY:
 * - 251105_1840 danielbeer: Added interactive hover, click handlers, and connected node highlighting
 * - 251031_2050 danielbeer: Initial creation
 * 
 * Circular layout with nodes arranged in concentric circles.
 * Best for: Visualizing distance from center, exploring neighborhoods
 */

import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import type { GraphNode, GraphEdge, GraphOptions, GraphCallbacks, GraphStyle, RadialLayoutOptions } from './types';

export interface RadialGraphProps<T = any> extends GraphOptions, GraphCallbacks<T> {
  data: {
    nodes: GraphNode<T>[];
    edges: GraphEdge[];
  };
  style?: GraphStyle;
  radialOptions?: RadialLayoutOptions;
  enableZoom?: boolean;  // Enable/disable zoom (default: false)
  className?: string;
}

export const RadialGraph = <T = any,>({
  data,
  width = 1200,
  height = 800,
  style = {},
  radialOptions: radialOptionsProp = {},
  onNodeClick,
  onNodeHover,
  onEdgeClick,
  onEdgeHover,
  highlightConnected = true,
  enableZoom = false,  // DISABLED BY DEFAULT
  className = '',
}: RadialGraphProps<T>) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredEdge, setHoveredEdge] = useState<string | null>(null);

  useEffect(() => {
    if (!svgRef.current || data.nodes.length === 0) return;

    d3.select(svgRef.current).selectAll('*').remove();
    const svg = d3.select(svgRef.current);
    const radialOptions = { centerNode: data.nodes[0]?.id, radius: 100, angleSpread: 360, ...radialOptionsProp };
    
    const container = svg.append('g').attr('transform', `translate(${width / 2},${height / 2})`);

    // Find center node
    const centerNodeId = radialOptions.centerNode || data.nodes[0]?.id;
    const centerNode = data.nodes.find(n => n.id === centerNodeId);

    if (!centerNode) return;

    // Calculate levels from center
    const levels = new Map<string, number>();
    const visited = new Set<string>();
    const queue: Array<{ id: string; level: number }> = [{ id: centerNode.id, level: 0 }];

    levels.set(centerNode.id, 0);
    visited.add(centerNode.id);

    while (queue.length > 0) {
      const { id, level } = queue.shift()!;
      
      data.edges.forEach(edge => {
        if (edge.source === id && !visited.has(edge.target)) {
          levels.set(edge.target, level + 1);
          visited.add(edge.target);
          queue.push({ id: edge.target, level: level + 1 });
        }
        if (edge.target === id && !visited.has(edge.source)) {
          levels.set(edge.source, level + 1);
          visited.add(edge.source);
          queue.push({ id: edge.source, level: level + 1 });
        }
      });
    }

    // Position nodes
    const positions = new Map<string, { x: number; y: number }>();
    const nodesByLevel = new Map<number, string[]>();

    levels.forEach((level, nodeId) => {
      if (!nodesByLevel.has(level)) nodesByLevel.set(level, []);
      nodesByLevel.get(level)!.push(nodeId);
    });

    nodesByLevel.forEach((nodeIds, level) => {
      const radius = level * radialOptions.radius;
      const angleStep = (radialOptions.angleSpread * Math.PI / 180) / Math.max(nodeIds.length, 1);
      const startAngle = -(radialOptions.angleSpread * Math.PI / 360);

      nodeIds.forEach((nodeId, index) => {
        const angle = startAngle + index * angleStep;
        positions.set(nodeId, {
          x: radius * Math.cos(angle),
          y: radius * Math.sin(angle),
        });
      });
    });

    // Optional zoom - only if enabled
    if (enableZoom) {
      const zoom = d3.zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.1, 4])
        .filter((event: any) => {
          const target = event.target;
          return target.tagName === 'svg';  // Only on background
        })
        .on('zoom', (event) => {
          container.attr('transform', `translate(${width / 2},${height / 2}) ${event.transform}`);
        });
      svg.call(zoom);
    }

    // Get connected nodes for highlighting
    const getConnectedNodes = (nodeId: string): Set<string> => {
      const connected = new Set<string>();
      data.edges.forEach(edge => {
        if (edge.source === nodeId) connected.add(edge.target);
        if (edge.target === nodeId) connected.add(edge.source);
      });
      return connected;
    };

    const connectedNodes = hoveredNode ? getConnectedNodes(hoveredNode) : new Set<string>();

    // Draw edges
    container.selectAll('.link')
      .data(data.edges)
      .join('line')
      .attr('class', 'link')
      .attr('x1', d => positions.get(d.source)?.x || 0)
      .attr('y1', d => positions.get(d.source)?.y || 0)
      .attr('x2', d => positions.get(d.target)?.x || 0)
      .attr('y2', d => positions.get(d.target)?.y || 0)
      .attr('stroke', d => {
        if (typeof style.edgeColor === 'function') return style.edgeColor(d);
        return d.color || style.edgeColor || '#999';
      })
      .attr('stroke-width', d => {
        const edgeId = `${d.source}-${d.target}`;
        if (hoveredEdge === edgeId) return 3;
        if (highlightConnected && hoveredNode && (d.source === hoveredNode || d.target === hoveredNode)) return 2.5;
        return 1.5;
      })
      .attr('stroke-opacity', d => {
        const edgeId = `${d.source}-${d.target}`;
        if (hoveredEdge === edgeId) return 0.8;
        if (highlightConnected && hoveredNode && (d.source === hoveredNode || d.target === hoveredNode)) return 0.6;
        return 0.3;
      })
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        const edgeId = `${d.source}-${d.target}`;
        setHoveredEdge(edgeId);
        if (onEdgeHover) onEdgeHover(d, event);
      })
      .on('mouseout', function(event) {
        setHoveredEdge(null);
        if (onEdgeHover) onEdgeHover(null, event);
      })
      .on('click', function(event, d) {
        if (onEdgeClick) onEdgeClick(d, event);
      });

    // Draw nodes
    const nodes = container.selectAll('.node')
      .data(data.nodes)
      .join('g')
      .attr('class', 'node')
      .attr('transform', d => {
        const pos = positions.get(d.id);
        return `translate(${pos?.x || 0},${pos?.y || 0})`;
      });

    nodes.append('circle')
      .attr('r', d => 
        typeof style.nodeSize === 'function' ? style.nodeSize(d) :
        d.size || style.nodeSize || (d.id === centerNodeId ? 25 : 15)
      )
      .attr('fill', d => 
        typeof style.nodeColor === 'function' ? style.nodeColor(d) :
        d.color || style.nodeColor || (d.id === centerNodeId ? '#ef4444' : '#3b82f6')
      )
      .attr('stroke', d => {
        if (d.id === hoveredNode) return '#fbbf24';
        if (highlightConnected && hoveredNode && connectedNodes.has(d.id)) return '#60a5fa';
        return '#fff';
      })
      .attr('stroke-width', d => {
        if (d.id === hoveredNode) return 4;
        if (highlightConnected && hoveredNode && connectedNodes.has(d.id)) return 3;
        return 2;
      })
      .attr('opacity', d => {
        if (!hoveredNode) return 1;
        if (d.id === hoveredNode || connectedNodes.has(d.id)) return 1;
        return 0.3;
      })
      .style('cursor', 'pointer')
      .on('mouseover', function(event, d) {
        setHoveredNode(d.id);
        if (onNodeHover) onNodeHover(d, event);
      })
      .on('mouseout', function(event) {
        setHoveredNode(null);
        if (onNodeHover) onNodeHover(null, event);
      })
      .on('click', function(event, d) {
        if (onNodeClick) onNodeClick(d, event);
      });

    nodes.append('text')
      .text(d => d.label)
      .attr('text-anchor', 'middle')
      .attr('dy', d => {
        const radius = typeof style.nodeSize === 'function' ? style.nodeSize(d) :
          d.size || style.nodeSize || (d.id === centerNodeId ? 25 : 15);
        return radius + 15;
      })
      .attr('font-size', style.fontSize || '11px')
      .attr('font-family', style.fontFamily || 'sans-serif')
      .attr('fill', '#333');

  }, [data, width, height, style, radialOptionsProp, hoveredNode, hoveredEdge, onNodeClick, onNodeHover, onEdgeClick, onEdgeHover, highlightConnected]);

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      className={`radial-graph ${className}`}
      style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        background: style.backgroundColor || '#fafafa',
      }}
    />
  );
};

export default RadialGraph;
